<script setup lang="ts">
    import { useLoadedProduct } from '@/composables/useLoadedProduct';
    import { EProductFormat } from '@/domain/product/EProductFormat';
    import { throttle } from '@/utils/throttle';
    import { useConfigStore } from '@/store/config.store';
    import { useEditor2D } from '@/composables/useEditor2D';
    import { _disposeEditor3D, useEditor3D } from '@/composables/useEditor3D';
    import { storeToRefs } from 'pinia';
    import { useElementSize } from '@vueuse/core';
    import { onMounted, onUnmounted, shallowRef, useTemplateRef, watch } from 'vue';
    import type { Preview3DController } from '@/application/Preview3DController';
    import type { TSide } from '@/domain/shared/TSide';

    const canvasRef = useTemplateRef<HTMLCanvasElement | null>('canvas');
    const containerRef = useTemplateRef<HTMLDivElement | null>('container');

    const { isMobile, productType, zoom3dFactor } = storeToRefs(useConfigStore());
    const product = useLoadedProduct();

    const controller = shallowRef<Preview3DController | null>(null);
    const isReady = shallowRef(false);
    const { height: containerHeight, width: containerWidth } = useElementSize(containerRef);
    let unsubscribeCallbacks: (() => void)[] = [];

    function wireTexturePush(preview: Preview3DController) {
        const sides: TSide[] = product.value.format === EProductFormat.RECTO_VERSO ? ['recto', 'verso'] : ['recto'];
        sides.forEach((side) => {
            const editor2D = useEditor2D(side);
            if (!editor2D) {
                return;
            }

            const push = throttle(
                async () => {
                    const snapshot = await editor2D.exports.exportPreviewZoneCanvas();
                    preview.applyCanvasTexture(side, snapshot);
                },
                150,
                { throttleTimer: false },
            );

            push();
            const unsubscribe = editor2D.onDidChange(() => push());
            unsubscribeCallbacks.push(unsubscribe);
        });
    }

    onMounted(async () => {
        if (!canvasRef.value || !containerRef.value) {
            return;
        }
        const binding = useEditor3D(canvasRef.value, {
            container: containerRef.value,
            isMobile: isMobile.value,
            urlProductType: productType.value,
        });
        if (!binding) {
            return;
        }
        controller.value = binding.controller;
        await binding.ready;
        isReady.value = binding.isReady.value;
        wireTexturePush(binding.controller);

        // Only now does the strategy root exist, so the framing distance is
        // known and the wheel can be bounded to the same range as the buttons.
        // Subscribed here rather than from the zoom UI: those components mount
        // before this one creates the editor, so they would silently subscribe
        // to nothing.
        binding.controller.camera.syncZoomBounds();
        zoom3dFactor.value = binding.controller.camera.getZoomFactor();
        unsubscribeCallbacks.push(
            binding.controller.camera.onCameraMoved(() => {
                if (binding.controller.camera.isFraming) {
                    return;
                }
                zoom3dFactor.value = binding.controller.camera.getZoomFactor();
            }),
        );
    });

    watch([containerWidth, containerHeight], () => {
        controller.value?.resize();
    });

    onUnmounted(() => {
        unsubscribeCallbacks.forEach((u) => u());
        unsubscribeCallbacks = [];
        _disposeEditor3D(canvasRef.value);
        controller.value = null;
    });

    defineExpose({ controller });
</script>

<template>
    <div
        ref="container"
        class="relative h-full w-full"
    >
        <canvas
            ref="canvas"
            data-test="webgl-canvas"
            :class="['h-full w-full transition-opacity duration-200', !isReady && 'opacity-0']"
        />
        <div
            v-if="!isReady"
            class="absolute inset-0 grid place-items-center"
            data-test="webgl-loader"
        >
            <div class="flex items-center gap-2">
                <div class="h-3 w-3 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]"></div>
                <div class="h-3 w-3 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]"></div>
                <div class="h-3 w-3 animate-bounce rounded-full bg-gray-400"></div>
            </div>
        </div>
        <slot :controller />
    </div>
</template>
